import type { datadogRum } from '@datadog/browser-rum'
import { AxiosError, type AxiosResponse } from 'axios'

import type { DatadogClient } from './datadogClient'

export const datadogClientAxiosErrorHandler =
  (datadogClient: DatadogClient) =>
  (axiosError: AxiosError, context?: Parameters<typeof datadogRum.addError>[1]) => {
    // (1) 취소된 요청은 무시
    if (
      !datadogClient.enabled ||
      !datadogClient.initialized ||
      axiosError.code === AxiosError.ERR_CANCELED
    )
      return

    const axiosErrorResponse = axiosError.response as
      | AxiosResponse<{ code: string; message: string }>
      | undefined

    // (2) whitelist : 응답이 없는 에러 무시, 400, 404 외의 에러는 무시
    if (
      !axiosErrorResponse ||
      !(axiosErrorResponse.status === 400 || axiosErrorResponse.status === 404)
    ) {
      return
    }

    const {
      status,
      config: { url, method, params },
    } = axiosErrorResponse

    const code = axiosErrorResponse.data?.code ?? 'UNKNOWN_CODE'
    const message = axiosErrorResponse.data?.message ?? 'Unknown error'

    // (3) 의도된 커스텀 에러 무시
    const isIgnored = datadogClient.networkErrorsToIgnore.some((ignoreError) => {
      if (ignoreError.status !== undefined && ignoreError.status !== status) return false
      if (ignoreError.url !== undefined && ignoreError.url !== url) return false
      if (ignoreError.method !== undefined && ignoreError.method !== method) return false
      if (ignoreError.code !== undefined && ignoreError.code !== code) return false
      return true
    })

    if (isIgnored) return

    context = {
      ...context,
      error: {
        kind: `[${status}] ${method?.toUpperCase()} ${url}`,
        code: code,
        status: status,
        message: message,
      },
      http: {
        method: method,
        url: url,
        status_code: status,
      },
      request: {
        _note: 'payload omitted to reduce log size',
        params: params ?? null,
      },
      response: {
        _note: 'data omitted to reduce log size',
      },
    }

    // 에러 메시지 커스터마이징
    if (axiosErrorResponse) {
      axiosError.message = `${code}: ${message}`
    }

    datadogClient.instance.addError(axiosError, context)
  }
